import type { ColumnOverrides } from './extractStudents';
import { parseCsvRecords } from './parseCsvRow';
import { ROSTER_FIELD_LABELS } from './rosterFieldLabels';
import { ID_COLUMNS } from './sisRoster';
import { sisMappingCache } from '../Data/sisMappingApi';

export type SisFieldTier = 'load_bearing' | 'helpful' | 'optional';
export type SisFieldKind = 'id' | 'date' | 'date_or_years' | 'boolean' | 'el_status' | 'text';

export type SisFieldKey =
  | 'student_id'
  | 'state_student_id'
  | 'el_service_start'
  | 'exit_date'
  | 'exit_reason'
  | 'school'
  | 'grade'
  | 'el_status'
  | 'program_model'
  | 'dually_identified'
  | 'iep'
  | 'section_504'
  | 'originating_district'
  | 'alternate_access'
  | 'newcomer_flag'
  | 'home_language'
  | 'assigned_teacher'
  | 'pronouns';

export type SisField = {
  key: SisFieldKey;
  label: string;
  consequence: string;
  tier: SisFieldTier;
  kind: SisFieldKind;
  exactHeaders: string[];
  blankImpact: string | null;
};

export const SIS_FIELDS: ReadonlyArray<SisField> = [
  {
    key: 'student_id',
    label: ROSTER_FIELD_LABELS.student_id,
    consequence: "Powers matching this file to your other files. Without it, students in this file can't be matched to your other uploads.",
    tier: 'load_bearing',
    kind: 'id',
    exactHeaders: ['student id'],
    blankImpact: "those students can't be matched to your other uploads.",
  },
  {
    key: 'state_student_id',
    label: ROSTER_FIELD_LABELS.state_student_id,
    consequence:
      'Powers matching to files that carry the state ID, such as ACCESS results, and to Flashlight360 where students are known by state ID.',
    tier: 'helpful',
    kind: 'id',
    exactHeaders: ['state student id', 'state id', 'ssid', 'sasid', 'state student identifier'],
    blankImpact: 'those students can only be matched by their district ID.',
  },
  {
    key: 'el_service_start',
    label: ROSTER_FIELD_LABELS.el_service_start,
    consequence: 'Powers newcomer and long-term EL status everywhere. A date or a years count both work.',
    tier: 'load_bearing',
    kind: 'date_or_years',
    exactHeaders: ['el service start date', 'el service start', 'years in el'],
    blankImpact: "those students won't be counted as newcomers or long-term ELs.",
  },
  {
    key: 'exit_date',
    label: ROSTER_FIELD_LABELS.exit_date,
    consequence:
      "Powers the exit rate and departures breakdown, and scopes both to this year. Without it, exits from earlier years are counted as this year's.",
    tier: 'load_bearing',
    kind: 'date',
    exactHeaders: ['exit date', 'el exit date'],
    blankImpact: null,
  },
  {
    key: 'exit_reason',
    label: ROSTER_FIELD_LABELS.exit_reason,
    consequence:
      'Splits the departures breakdown by reason. Without it, a student with an exit date is classified from their ACCESS scores and EL status: scores that met your state’s exit criteria, or a status of monitoring or reclassified, count as meeting criteria, and anything else counts as leaving for other reasons. Without an exit date either, we derive exits from testing history, which needs WIDA scores from earlier years and cannot see students who left the district entirely.',
    tier: 'helpful',
    kind: 'text',
    exactHeaders: [
      'exit reason',
      'el exit reason',
      'lep exit reason',
      'el exit code',
      'exit code',
      'program exit code',
      'exit type',
      'ell exit type',
      'reclassification reason',
      'withdrawal reason',
    ],
    blankImpact: null,
  },
  {
    key: 'school',
    label: ROSTER_FIELD_LABELS.school,
    consequence: 'Powers school-by-school comparison, per-school findings, and the plan header.',
    tier: 'load_bearing',
    kind: 'text',
    exactHeaders: ['school', 'school assignment', 'school name'],
    blankImpact: 'those students fall back to the school in your WIDA file, if it has one.',
  },
  {
    key: 'grade',
    label: ROSTER_FIELD_LABELS.grade,
    consequence: 'Powers the plan header and grade-band descriptors when WIDA lacks a grade.',
    tier: 'helpful',
    kind: 'text',
    exactHeaders: ['grade', 'grade level'],
    blankImpact: 'those students fall back to the grade in your WIDA file, if it has one.',
  },
  {
    key: 'el_status',
    label: ROSTER_FIELD_LABELS.el_status,
    consequence: 'Powers the status shown on the plan header (Active, Former EL, or Monitoring).',
    tier: 'helpful',
    kind: 'el_status',
    exactHeaders: ['el status'],
    blankImpact: 'those students’ plan headers show the default "Active" status.',
  },
  {
    key: 'program_model',
    label: ROSTER_FIELD_LABELS.program_model,
    consequence: 'Powers per-student program on plans and program-model patterns in findings.',
    tier: 'helpful',
    kind: 'text',
    exactHeaders: ['program model'],
    blankImpact:
      'those students’ plan headers leave out program model, and they drop out of program-model patterns in findings.',
  },
  {
    key: 'dually_identified',
    label: ROSTER_FIELD_LABELS.dually_identified,
    consequence: 'Powers the dually-identified cohort in subgroup findings.',
    tier: 'helpful',
    kind: 'boolean',
    exactHeaders: ['dually identified', 'dually identified flag'],
    blankImpact: null,
  },
  {
    key: 'originating_district',
    label: ROSTER_FIELD_LABELS.originating_district,
    consequence: 'Powers the transferred-in vs. in-district split behind newcomer and LTEL analysis.',
    tier: 'optional',
    kind: 'text',
    exactHeaders: ['originating district'],
    blankImpact: null,
  },
  {
    key: 'alternate_access',
    label: ROSTER_FIELD_LABELS.alternate_access,
    consequence: 'Powers the Alternate ACCESS cohort baseline.',
    tier: 'optional',
    kind: 'boolean',
    exactHeaders: ['alternate access', 'alternate access flag'],
    blankImpact: null,
  },
  {
    key: 'newcomer_flag',
    label: ROSTER_FIELD_LABELS.newcomer_flag,
    consequence: "Powers the newcomer count directly when your district tracks newcomers by a flag rather than by years in EL.",
    tier: 'helpful',
    kind: 'boolean',
    exactHeaders: ['newcomer', 'newcomer flag', 'intake flag'],
    blankImpact: 'the newcomer count falls back to years in the program for those students.',
  },
  {
    key: 'home_language',
    label: ROSTER_FIELD_LABELS.home_language,
    consequence: 'Adds home language to the plan header.',
    tier: 'optional',
    kind: 'text',
    exactHeaders: ['home language'],
    blankImpact: 'those students fall back to the home language in your WIDA file, if it has one.',
  },
  {
    key: 'assigned_teacher',
    label: ROSTER_FIELD_LABELS.assigned_teacher,
    consequence: 'Adds the assigned teacher to the plan header.',
    tier: 'optional',
    kind: 'text',
    exactHeaders: ['assigned el teacher', 'assigned teacher', 'teacher'],
    blankImpact: 'those students’ plan headers leave out the EL teacher.',
  },
  {
    key: 'iep',
    label: ROSTER_FIELD_LABELS.iep,
    consequence: 'Shows IEP on file in the plan header and opens the ACCESS accommodations list on the plan.',
    tier: 'optional',
    kind: 'boolean',
    exactHeaders: ['iep', 'iep status', 'iep flag', 'has iep'],
    blankImpact: 'those students’ plans cannot carry ACCESS accommodations.',
  },
  {
    key: 'section_504',
    label: ROSTER_FIELD_LABELS.section_504,
    consequence: 'Shows Section 504 on file in the plan header and opens the ACCESS accommodations list on the plan.',
    tier: 'optional',
    kind: 'boolean',
    exactHeaders: ['section 504', '504', '504 plan', 'section 504 plan'],
    blankImpact: 'those students’ plans cannot carry ACCESS accommodations.',
  },
  {
    key: 'pronouns',
    label: ROSTER_FIELD_LABELS.pronouns,
    consequence: 'Lets plan lead sentences use pronouns; without it they fall back to the student name.',
    tier: 'optional',
    kind: 'text',
    exactHeaders: ['pronouns'],
    blankImpact: null,
  },
];

export type SisColumn = {
  index: number;
  header: string;
  samples: string[];
  blankShare: number;
};

export type SisMapping = Partial<Record<SisFieldKey, number>>;

/** Saved for a field the district said is not in the file; only the newcomer flag uses it, since older mappings omit the key for a different reason. */
export const NOT_IN_FILE = -1;

export function absentMappingValue(key: SisFieldKey): number | undefined {
  return key === 'newcomer_flag' || key === 'iep' || key === 'section_504' ? NOT_IN_FILE : undefined;
}

export function sisColumnOverrides(mapping: SisMapping): ColumnOverrides {
  return {
    districtIdIndex: mapping.student_id,
    stateIdIndex: mapping.state_student_id,
    schoolIndex: mapping.school,
  };
}

export type SisMappingWarning =
  | { code: 'mostly_blank'; field: SisFieldKey; blankShare: number }
  | { code: 'unparseable'; field: SisFieldKey; affectedRows: number }
  | { code: 'no_recognized_values'; field: SisFieldKey };

const MAX_SAMPLES = 5;

export const MOSTLY_BLANK_FLOOR = 0.2;

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

export function readSisColumns(csvText: string): SisColumn[] {
  const records = parseCsvRecords(csvText);
  const headers = records[0];
  if (!headers) return [];
  const rows = records.slice(1);
  return headers.map((header, index) => {
    const values = rows.map((row) => (row[index] ?? '').trim());
    const nonBlank = values.filter((v) => v !== '');
    return {
      index,
      header: header.trim(),
      samples: nonBlank.slice(0, MAX_SAMPLES),
      blankShare: values.length === 0 ? 0 : (values.length - nonBlank.length) / values.length,
    };
  });
}

// The same shapes schoolYearEndFromDate reads: a trailing time is accepted,
// and so is the DD-Mon-YYYY spelling, since real SIS exports emit both.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?Z?)?$/;
const SLASH_DATE = /^\d{1,2}\/\d{1,2}\/\d{2,4}(?:\s+\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?)?$/;
const DMY_DATE =
  /^\d{1,2}-(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)-\d{4}(?:\s+\d{1,2}:\d{2}(?::\d{2})?)?$/i;
const ID_SHAPE = /^[a-z0-9][a-z0-9._-]*$/i;
const YEARS_SHAPE = /^\d{1,2}$/;
const BARE_YEAR_SHAPE = /^(19|20)\d{2}$/;
const BOOLEAN_VALUES = new Set(['yes', 'no', 'y', 'n', 'true', 'false', '1', '0']);
// "exit" covers Kentucky's "EL Exited" (Infinite Campus), which the server
// normalises to reclassified.
const EL_STATUS_PHRASES = ['monitor', 'reclass', 'exit', 'inactive', 'not active', 'non-active', 'non active', 'active'];

function readsAsELStatus(value: string): boolean {
  const v = normalize(value);
  return EL_STATUS_PHRASES.some((phrase) => v.includes(phrase));
}

const EXIT_REASON_RECOGNIZED = new Set([
  'reclassified',
  'reclass',
  'rfep',
  'fep',
  'fluent english proficient',
  'met exit criteria',
  'met criteria',
  'exited',
  'exited - met criteria',
  'proficient',
  'moved',
  'moved out of district',
  'moved out',
  'transferred',
  'transfer',
  'left district',
]);

function isDateLike(value: string): boolean {
  return ISO_DATE.test(value) || SLASH_DATE.test(value) || DMY_DATE.test(value);
}

export function valueShapeHint(samples: string[]): 'dates' | 'ids' | null {
  if (samples.length === 0) return null;
  if (samples.every(isDateLike)) return 'dates';
  if (samples.every((s) => ID_SHAPE.test(s) && /\d/.test(s))) return 'ids';
  return null;
}

export function proposeSisMapping(columns: SisColumn[]): SisMapping {
  const mapping: SisMapping = {};
  const taken = new Set<number>();
  for (const field of SIS_FIELDS) {
    const match = columns.find(
      (col) => !taken.has(col.index) && field.exactHeaders.includes(normalize(col.header))
    );
    if (match) {
      mapping[field.key] = match.index;
      taken.add(match.index);
    }
  }
  return mapping;
}

export function columnSignature(headers: string[]): string {
  return headers.map(normalize).join('\u001f');
}

function parsesAs(kind: SisFieldKind, value: string): boolean {
  switch (kind) {
    case 'date':
      return isDateLike(value);
    case 'date_or_years':
      return isDateLike(value) || YEARS_SHAPE.test(value) || BARE_YEAR_SHAPE.test(value);
    case 'boolean':
      return BOOLEAN_VALUES.has(normalize(value));
    case 'el_status':
      return readsAsELStatus(value);
    default:
      return true;
  }
}

export function validateSisMapping(csvText: string, mapping: SisMapping): SisMappingWarning[] {
  const records = parseCsvRecords(csvText);
  const rows = records.slice(1);
  if (rows.length === 0) return [];
  const warnings: SisMappingWarning[] = [];
  for (const field of SIS_FIELDS) {
    const index = mapping[field.key];
    if (index === undefined || index < 0) continue;
    const values = rows.map((row) => (row[index] ?? '').trim());
    const nonBlank = values.filter((v) => v !== '');
    const blankShare = (values.length - nonBlank.length) / values.length;
    if (field.blankImpact !== null && blankShare >= MOSTLY_BLANK_FLOOR) {
      warnings.push({ code: 'mostly_blank', field: field.key, blankShare });
    }
    const affectedRows = nonBlank.filter((v) => !parsesAs(field.kind, v)).length;
    if (affectedRows > 0) {
      warnings.push({ code: 'unparseable', field: field.key, affectedRows });
    }
    if (field.key === 'exit_reason') {
      const headers = (records[0] ?? []).map((h) => normalize(h));
      const idIndexes = new Set<number>(
        headers.flatMap((h, i) => (ID_COLUMNS.includes(h) ? [i] : [])),
      );
      if (mapping.student_id !== undefined) idIndexes.add(mapping.student_id);
      if (mapping.state_student_id !== undefined) idIndexes.add(mapping.state_student_id);
      const studentRows =
        idIndexes.size === 0
          ? rows
          : rows.filter((row) => [...idIndexes].some((i) => (row[i] ?? '').trim() !== ''));
      const reasons = studentRows.map((row) => (row[index] ?? '').trim()).filter((v) => v !== '');
      if (reasons.length > 0 && !reasons.some((v) => EXIT_REASON_RECOGNIZED.has(normalize(v)))) {
        warnings.push({ code: 'no_recognized_values', field: field.key });
      }
    }
  }
  return warnings;
}

const STORAGE_KEY = 'bamboo.sisColumnMapping.v1';

export type SisMappingStorage = Pick<Storage, 'getItem' | 'setItem'>;

type SavedSisMapping = { headers: string[]; mapping: SisMapping };
type SisMappingStore = { latest: string; bySignature: Record<string, SavedSisMapping> };

function defaultStorage(): SisMappingStorage | null {
  return sisMappingCache;
}

function loadStore(store: SisMappingStorage | null): SisMappingStore | null {
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SisMappingStore;
    return parsed && typeof parsed === 'object' && parsed.bySignature ? parsed : null;
  } catch {
    return null;
  }
}

export function saveSisMapping(
  headers: string[],
  mapping: SisMapping,
  store: SisMappingStorage | null = defaultStorage()
): void {
  if (!store) return;
  const signature = columnSignature(headers);
  const existing = loadStore(store) ?? { latest: signature, bySignature: {} };
  existing.latest = signature;
  existing.bySignature[signature] = { headers, mapping };
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    return;
  }
}

export function loadSisMapping(
  signature: string,
  store: SisMappingStorage | null = defaultStorage()
): SisMapping | null {
  return loadStore(store)?.bySignature[signature]?.mapping ?? null;
}

export type SavedMappingResult =
  | { kind: 'none' }
  | { kind: 'exact'; mapping: SisMapping }
  | { kind: 'partial'; mapping: SisMapping; changedFields: SisFieldKey[] };

export function applySavedSisMapping(
  columns: SisColumn[],
  storage: SisMappingStorage | null = defaultStorage()
): SavedMappingResult {
  const store = loadStore(storage);
  if (!store) return { kind: 'none' };
  const signature = columnSignature(columns.map((c) => c.header));
  const exact = store.bySignature[signature];
  if (exact) return { kind: 'exact', mapping: exact.mapping };
  const saved = store.bySignature[store.latest];
  if (!saved) return { kind: 'none' };

  const byHeader = new Map(columns.map((c) => [normalize(c.header), c.index]));
  const mapping: SisMapping = {};
  const changedFields: SisFieldKey[] = [];
  for (const field of SIS_FIELDS) {
    const savedIndex = saved.mapping[field.key];
    if (savedIndex === undefined) continue;
    if (savedIndex < 0) {
      mapping[field.key] = savedIndex;
      continue;
    }
    const savedHeader = saved.headers[savedIndex];
    const newIndex = savedHeader === undefined ? undefined : byHeader.get(normalize(savedHeader));
    if (newIndex === undefined) {
      changedFields.push(field.key);
    } else {
      mapping[field.key] = newIndex;
    }
  }
  return { kind: 'partial', mapping, changedFields };
}
